import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskService } from '../services/taskService';
import { adminService } from '../services/adminService';
import { userService } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Loading from '../components/common/Loading';
import InlineToast from '../components/common/InlineToast';
import TableSkeleton from '../components/common/TableSkeleton';
import MultiSelectDropdown from '../components/common/MultiSelectDropdown';

const TasksPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const canManageGovernance = hasRole('Manager') || hasRole('Admin') || hasRole('Executive');
  const canEditTaskStatus = hasRole('Manager') || hasRole('Admin');
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    clientId: '',
    categoryId: '',
    hierarchyLevel: 'epic',
    parentTaskId: '',
    priority: 'P2',
    status: 'Not Started',
    targetDate: '',
    revisedDate: '',
    size: 'M',
    customerId: '',
    externalLinkUrl: '',
    externalLinkType: '',
    assigneeId: '',
    subAssigneeIds: [],
    dueDateChangeReasonTag: '',
    dueDateChangeReasonNotes: '',
    originalTargetDate: '',
    originalRevisedDate: ''
  });
  
  // Filter states
  const [filterClient, setFilterClient] = useState('');
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterAssignee, setFilterAssignee] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAssignment, setFilterAssignment] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [toast, setToast] = useState({ message: '', type: 'info' });
  const [initialTaskSnapshot, setInitialTaskSnapshot] = useState(null);
  const [updatingAssignmentTaskId, setUpdatingAssignmentTaskId] = useState(null);

  const resetTaskForm = () => {
    setIsEditing(false);
    setNewTask({
      title: '',
      clientId: '',
      categoryId: '',
      hierarchyLevel: 'epic',
      parentTaskId: '',
      priority: 'P2',
      status: 'Not Started',
      targetDate: '',
      revisedDate: '',
      size: 'M',
      customerId: '',
      externalLinkUrl: '',
      externalLinkType: '',
      assigneeId: '',
      subAssigneeIds: [],
      dueDateChangeReasonTag: '',
      dueDateChangeReasonNotes: '',
      originalTargetDate: '',
      originalRevisedDate: ''
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksRes, clientsRes, categoriesRes, usersRes] = await Promise.all([
        taskService.getTasks(),
        adminService.getClients(),
        adminService.getCategories(),
        userService.getAllUsers()
      ]);
      
      const tasksData = tasksRes.data || [];
      setAllTasks(tasksData);
      setTasks(tasksData);
      setClients(clientsRes.data || []);
      setCategories(categoriesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      setToast({ message: 'Failed to load data.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...allTasks];

    if (filterClient) {
      filtered = filtered.filter(task => task.client?.id === parseInt(filterClient));
    }

    if (filterCategory.length > 0) {
      filtered = filtered.filter(task => filterCategory.includes(String(task.category?.id)));
    }

    if (filterAssignee.length > 0) {
      filtered = filtered.filter(task => filterAssignee.includes(String(task.assignee?.id)));
    }

    if (filterStatus) {
      filtered = filtered.filter(task => task.status === filterStatus);
    }

    if (filterAssignment) {
      filtered = filtered.filter(task => (task.assignmentState || 'inactive') === filterAssignment);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(term) ||
        (task.assignee?.name || '').toLowerCase().includes(term)
      );
    }

    const priorityOrder = ['P1', 'P2', 'P3', 'P4'];
    const statusOrder = [
      'Not Started',
      'Planning',
      'In Progress',
      'In Review',
      'Blocked',
      'On Hold',
      'Completed',
      'Cancelled'
    ];

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal;
        let bVal;

        if (sortConfig.key === 'priority') {
          aVal = priorityOrder.indexOf(a.priority);
          bVal = priorityOrder.indexOf(b.priority);
        } else if (sortConfig.key === 'status') {
          aVal = statusOrder.indexOf(a.status || 'Not Started');
          bVal = statusOrder.indexOf(b.status || 'Not Started');
        } else if (sortConfig.key === 'targetDate') {
          aVal = a.targetDate ? new Date(a.targetDate).getTime() : Infinity;
          bVal = b.targetDate ? new Date(b.targetDate).getTime() : Infinity;
        } else if (sortConfig.key === 'assignmentState') {
          aVal = (a.assignmentState || 'inactive') === 'active' ? 0 : 1;
          bVal = (b.assignmentState || 'inactive') === 'active' ? 0 : 1;
        } else {
          return 0;
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setTasks(filtered);
  }, [
    allTasks,
    filterClient,
    filterCategory,
    filterAssignee,
    filterStatus,
    filterAssignment,
    searchTerm,
    sortConfig
  ]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const toggleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ column }) => {
    const active = sortConfig.key === column;
    const asc = active && sortConfig.direction === 'asc';
    const desc = active && sortConfig.direction === 'desc';
    return (
      <span className="inline-flex flex-col ml-1 gap-px align-middle">
        <svg className={`w-2.5 h-2.5 ${asc ? 'text-blue-600' : 'text-gray-300'}`} viewBox="0 0 10 6" fill="currentColor">
          <path d="M5 0L10 6H0L5 0Z" />
        </svg>
        <svg className={`w-2.5 h-2.5 ${desc ? 'text-blue-600' : 'text-gray-300'}`} viewBox="0 0 10 6" fill="currentColor">
          <path d="M5 6L0 0H10L5 6Z" />
        </svg>
      </span>
    );
  };

  const isOverdue = (task) => {
    const effectiveDueDate = task.revisedDate || task.targetDate;
    if (!effectiveDueDate) return false;
    if (task.status === 'Completed') return false;
    const today = new Date();
    const target = new Date(effectiveDueDate);
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return target < today;
  };

  const handleAssignmentStateChange = async (taskId, assignmentState, event) => {
    event.stopPropagation();
    try {
      setUpdatingAssignmentTaskId(taskId);
      await taskService.updateTask(taskId, { assignmentState });
      setToast({ message: `Task marked as ${assignmentState}.`, type: 'success' });
      await loadData();
    } catch (error) {
      setToast({ message: error.response?.data?.message || 'Failed to update assignment state.', type: 'error' });
    } finally {
      setUpdatingAssignmentTaskId(null);
    }
  };

  const handleSubmitTask = async (e) => {
    e.preventDefault();
    try {
      if (!isEditing && !canManageGovernance) {
        setToast({ message: 'Only Manager/Admin/Executive can create projects.', type: 'error' });
        return;
      }

      const dueDateChanged = isEditing && newTask.revisedDate !== (newTask.originalRevisedDate || '');
      if (canManageGovernance && dueDateChanged && !newTask.dueDateChangeReasonTag) {
        setToast({ message: 'Revised date change reason is required.', type: 'error' });
        return;
      }

      const payload = {
        title: newTask.title,
        clientId: newTask.clientId,
        categoryId: newTask.categoryId || null,
        hierarchyLevel: newTask.hierarchyLevel,
        parentTaskId: newTask.hierarchyLevel === 'epic' ? null : (newTask.parentTaskId || null),
        priority: newTask.priority,
        status: newTask.status,
        targetDate: newTask.targetDate || null,
        revisedDate: newTask.revisedDate || null,
        size: newTask.size || null,
        customerId: newTask.customerId || null,
        assigneeId: newTask.assigneeId || null,
        subAssigneeIds: newTask.subAssigneeIds || []
      };
      if (dueDateChanged) {
      payload.dueDateChangeReasonTag = newTask.dueDateChangeReasonTag;
      payload.dueDateChangeReasonNotes = newTask.dueDateChangeReasonNotes || null;
      }
      payload.externalLinkUrl = newTask.externalLinkUrl || null;
      payload.externalLinkType = newTask.externalLinkUrl ? (newTask.externalLinkType || null) : null;

      if (isEditing) {
        await taskService.updateTask(newTask.id, payload);
      } else {
        await taskService.createTask(payload);
      }
      setShowModal(false);
      setInitialTaskSnapshot(null);
      resetTaskForm();
      setToast({ message: isEditing ? 'Task updated successfully.' : 'Task created successfully.', type: 'success' });
      loadData();
    } catch (error) {
      setToast({ message: error.response?.data?.message || 'Failed to save task.', type: 'error' });
    }
  };

  const handleTaskClick = (taskId) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleEditTask = (task, event) => {
    event.stopPropagation();
    setIsEditing(true);
    setNewTask({
      id: task.id,
      title: task.title,
      clientId: task.client?.id || '',
      categoryId: task.category?.id || '',
      hierarchyLevel: task.hierarchyLevel,
      parentTaskId: task.parentTask?.id || '',
      priority: task.priority || 'P2',
      status: task.status || 'Not Started',
      targetDate: task.targetDate || '',
      revisedDate: task.revisedDate || '',
      size: task.size || 'M',
      customerId: task.customerId || '',
      externalLinkUrl: task.externalLinkUrl || '',
      externalLinkType: task.externalLinkType || '',
      assigneeId: task.assignee?.id || '',
      subAssigneeIds: task.subAssignees?.map(user => String(user.id)) || [],
      dueDateChangeReasonTag: '',
      dueDateChangeReasonNotes: '',
      originalTargetDate: task.targetDate || '',
      originalRevisedDate: task.revisedDate || ''
    });
    setInitialTaskSnapshot(JSON.stringify({
      title: task.title || '',
      clientId: String(task.client?.id || ''),
      categoryId: String(task.category?.id || ''),
      hierarchyLevel: task.hierarchyLevel || 'epic',
      parentTaskId: String(task.parentTask?.id || ''),
      priority: task.priority || 'P2',
      status: task.status || 'Not Started',
      targetDate: task.targetDate || '',
      revisedDate: task.revisedDate || '',
      size: task.size || 'M',
      customerId: task.customerId || '',
      externalLinkUrl: task.externalLinkUrl || '',
      externalLinkType: task.externalLinkType || '',
      assigneeId: String(task.assignee?.id || ''),
      subAssigneeIds: task.subAssignees?.map(user => String(user.id)) || []
    }));
    setShowModal(true);
  };

  const handleCreateTask = () => {
    resetTaskForm();
    setInitialTaskSnapshot(JSON.stringify({
      title: '',
      clientId: '',
      categoryId: '',
      hierarchyLevel: 'epic',
      parentTaskId: '',
      priority: 'P2',
      status: 'Not Started',
      targetDate: '',
      revisedDate: '',
      size: 'M',
      customerId: '',
      externalLinkUrl: '',
      externalLinkType: '',
      assigneeId: '',
      subAssigneeIds: []
    }));
    setShowModal(true);
  };

  const closeTaskModal = () => {
    const currentSnapshot = JSON.stringify({
      title: newTask.title || '',
      clientId: String(newTask.clientId || ''),
      categoryId: String(newTask.categoryId || ''),
      hierarchyLevel: newTask.hierarchyLevel || 'epic',
      parentTaskId: String(newTask.parentTaskId || ''),
      priority: newTask.priority || 'P2',
      status: newTask.status || 'Not Started',
      targetDate: newTask.targetDate || '',
      revisedDate: newTask.revisedDate || '',
      size: newTask.size || 'M',
      customerId: newTask.customerId || '',
      externalLinkUrl: newTask.externalLinkUrl || '',
      externalLinkType: newTask.externalLinkType || '',
      assigneeId: String(newTask.assigneeId || ''),
      subAssigneeIds: newTask.subAssigneeIds || []
    });

    if (initialTaskSnapshot && initialTaskSnapshot !== currentSnapshot) {
      const confirmClose = window.confirm('You have unsaved changes. Discard them?');
      if (!confirmClose) return;
    }

    setShowModal(false);
    setInitialTaskSnapshot(null);
    resetTaskForm();
  };

  const parentTaskOptions = allTasks
    .filter(t => {
      if (newTask.hierarchyLevel === 'story') return t.hierarchyLevel === 'epic';
      if (newTask.hierarchyLevel === 'subtask') return t.hierarchyLevel === 'story';
      return false;
    })
    .map(t => ({ value: t.id, label: t.title }));

  if (loading) {
    return (
      <div>
        <Loading />
        <div className="mt-4">
          <TableSkeleton rows={8} columns={9} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Tasks</h1>
        {canManageGovernance && (
          <Button onClick={handleCreateTask}>Create Task</Button>
        )}
      </div>

      <div className="mb-4">
        <InlineToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'info' })}
        />
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-20 bg-gray-50 pb-3">
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Filters</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <MultiSelectDropdown
                options={categories.map(c => ({ value: String(c.id), label: c.name }))}
                value={filterCategory}
                onChange={setFilterCategory}
                placeholder="All Categories"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
              <MultiSelectDropdown
                options={users.map(u => ({ value: String(u.id), label: u.fullName || u.name || u.email || `User ${u.id}` }))}
                value={filterAssignee}
                onChange={setFilterAssignee}
                placeholder="All Assignees"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="Not Started">Not Started</option>
                <option value="Planning">Planning</option>
                <option value="In Progress">In Progress</option>
                <option value="In Review">In Review</option>
                <option value="Blocked">Blocked</option>
                <option value="On Hold">On Hold</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignment</label>
              <select
                value={filterAssignment}
                onChange={(e) => setFilterAssignment(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by task name..."
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4 text-sm text-gray-600">
        Showing {tasks.length} of {allTasks.length} tasks
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {allTasks.length === 0 ? 'No tasks found. Create your first task!' : 'No tasks match your filters.'}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => toggleSort('priority')}
                >
                  <span className="inline-flex items-center">Priority <SortIcon column="priority" /></span>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => toggleSort('status')}
                >
                  <span className="inline-flex items-center">Status <SortIcon column="status" /></span>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => toggleSort('assignmentState')}
                >
                  <span className="inline-flex items-center">Assignment <SortIcon column="assignmentState" /></span>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => toggleSort('targetDate')}
                >
                  <span className="inline-flex items-center">Target / Revised <SortIcon column="targetDate" /></span>
                </th>
                {canManageGovernance && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map(task => (
                <tr 
                  key={task.id}
                  onClick={() => handleTaskClick(task.id)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-primary hover:underline align-top">
                    <div className="whitespace-normal break-words leading-snug">
                      {task.title}
                    </div>
                    {task.externalLinkUrl && (
                      <a
                        href={task.externalLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center text-xs text-blue-600 hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {task.externalLinkType ? `${task.externalLinkType.toUpperCase()} link` : 'External link'}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="max-w-[10rem] truncate">{task.client?.name || '-'}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="max-w-[10rem] truncate">{task.category?.name || '-'}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {task.assignee?.name ? (
                      <div className="max-w-[10rem] leading-tight">
                        <div className="truncate">{task.assignee.name.split(' ')[0] || '-'}</div>
                        <div className="truncate">{task.assignee.name.split(' ').slice(1).join(' ') || ''}</div>
                      </div>
                    ) : (
                      <div className="max-w-[10rem] truncate">-</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                      {task.hierarchyLevel}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs rounded ${
                      task.priority === 'P1' ? 'bg-red-100 text-red-800' :
                      task.priority === 'P2' ? 'bg-orange-100 text-orange-800' :
                      task.priority === 'P3' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs rounded ${
                      task.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      task.status === 'In Review' ? 'bg-purple-100 text-purple-800' :
                      task.status === 'Blocked' ? 'bg-red-100 text-red-800' :
                      task.status === 'Planning' ? 'bg-yellow-100 text-yellow-800' :
                      task.status === 'On Hold' ? 'bg-orange-100 text-orange-800' :
                      task.status === 'Cancelled' ? 'bg-gray-200 text-gray-700' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.status || 'Not Started'}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    {canEditTaskStatus ? (
                      <select
                        className={`border rounded px-2 py-1 text-xs font-medium ${
                          (task.assignmentState || 'inactive') === 'active'
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}
                        value={task.assignmentState || 'inactive'}
                        disabled={updatingAssignmentTaskId === task.id}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => handleAssignmentStateChange(task.id, event.target.value, event)}
                      >
                        <option value="inactive">Inactive</option>
                        <option value="active">Active</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 text-xs rounded font-medium ${
                        (task.assignmentState || 'inactive') === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {(task.assignmentState || 'inactive') === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-start gap-2">
                      <div className="leading-tight">
                        <div>{task.targetDate || '-'}</div>
                        {task.revisedDate && (
                          <div className="text-xs text-indigo-700">Revised: {task.revisedDate}</div>
                        )}
                      </div>
                      {isOverdue(task) && (
                        <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
                          Overdue
                        </span>
                      )}
                    </div>
                  </td>
                  {canManageGovernance && (
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <Button size="sm" variant="secondary" onClick={(event) => handleEditTask(task, event)}>
                        Edit
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={closeTaskModal}
        title={isEditing ? 'Edit Task' : 'Create New Task'}
      >
        <form onSubmit={handleSubmitTask}>
          <Input
            label="Task Title"
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            required
          />

          <Select
            label="Client"
            value={newTask.clientId}
            onChange={(e) => setNewTask({ ...newTask, clientId: e.target.value })}
            options={clients.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Select a client"
            required
          />

          <Select
            label="Category (Optional)"
            value={newTask.categoryId}
            onChange={(e) => setNewTask({ ...newTask, categoryId: e.target.value })}
            options={categories.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Select a category"
          />

          <Select
            label="Assignee"
            value={newTask.assigneeId}
            onChange={(e) => setNewTask({ ...newTask, assigneeId: e.target.value })}
            options={users.map(u => ({ value: u.id, label: u.fullName || u.name || u.email || `User ${u.id}` }))}
            placeholder="Select an assignee"
            required
            disabled={!canManageGovernance}
          />

          <Select
            label="Sub-Assignees"
            name="subAssigneeIds"
            multiple
            value={newTask.subAssigneeIds}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions).map(option => option.value);
              setNewTask({ ...newTask, subAssigneeIds: values });
            }}
            options={users.map(u => ({ value: String(u.id), label: u.fullName || u.name || u.email || `User ${u.id}` }))}
          />

          <Select
            label="Priority"
            value={newTask.priority}
            onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
            options={[
              { value: 'P1', label: 'P1 - Critical' },
              { value: 'P2', label: 'P2 - High' },
              { value: 'P3', label: 'P3 - Medium' },
              { value: 'P4', label: 'P4 - Low' }
            ]}
            required
            disabled={!canManageGovernance}
          />

          <Select
            label="Status"
            value={newTask.status}
            onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
            options={[
              { value: 'Not Started', label: 'Not Started' },
              { value: 'Planning', label: 'Planning' },
              { value: 'In Progress', label: 'In Progress' },
              { value: 'In Review', label: 'In Review' },
              { value: 'Blocked', label: 'Blocked' },
              { value: 'On Hold', label: 'On Hold' },
              { value: 'Completed', label: 'Completed' },
              { value: 'Cancelled', label: 'Cancelled' }
            ]}
            required
            disabled={!canEditTaskStatus}
          />

          <Input
            label="Target Date"
            type="date"
            value={newTask.targetDate}
            onChange={(e) => setNewTask({ ...newTask, targetDate: e.target.value })}
            disabled={!canManageGovernance}
          />

          <Input
            label="Revised Date"
            type="date"
            value={newTask.revisedDate}
            onChange={(e) => setNewTask({ ...newTask, revisedDate: e.target.value })}
            disabled={!canManageGovernance}
          />

          {canManageGovernance && isEditing && isOverdue({ ...newTask, targetDate: newTask.targetDate, revisedDate: newTask.revisedDate }) && !newTask.revisedDate && (
            <p className="text-sm text-amber-700 mb-4">
              Task is overdue. Set a Revised Date and provide a change reason.
            </p>
          )}

          <Select
            label="Size"
            value={newTask.size}
            onChange={(e) => setNewTask({ ...newTask, size: e.target.value })}
            options={[
              { value: 'XS', label: 'XS' },
              { value: 'S', label: 'S' },
              { value: 'M', label: 'M' },
              { value: 'L', label: 'L' },
              { value: 'XL', label: 'XL' }
            ]}
            required
            disabled={!canManageGovernance}
          />

          <Select
            label="External Link Type (Optional)"
            value={newTask.externalLinkType}
            onChange={(e) => setNewTask({ ...newTask, externalLinkType: e.target.value })}
            options={[
              { value: 'github', label: 'GitHub' },
              { value: 'gitlab', label: 'GitLab' },
              { value: 'jira', label: 'JIRA' },
              { value: 'other', label: 'Other' }
            ]}
            placeholder="Select link type"
            disabled={!canManageGovernance}
          />

          <Input
            label="External Link URL (Optional)"
            type="url"
            value={newTask.externalLinkUrl}
            onChange={(e) => setNewTask({ ...newTask, externalLinkUrl: e.target.value })}
            placeholder="https://..."
            disabled={!canManageGovernance}
          />

          <Select
            label="Hierarchy Level"
            value={newTask.hierarchyLevel}
            onChange={(e) => setNewTask({ ...newTask, hierarchyLevel: e.target.value, parentTaskId: '' })}
            options={[
              { value: 'epic', label: 'Epic' },
              { value: 'story', label: 'Story' },
              { value: 'subtask', label: 'Subtask' }
            ]}
            required
          />

          {newTask.hierarchyLevel !== 'epic' && parentTaskOptions.length > 0 && (
            <Select
              label="Parent Task"
              value={newTask.parentTaskId}
              onChange={(e) => setNewTask({ ...newTask, parentTaskId: e.target.value })}
            options={parentTaskOptions}
            placeholder="Select parent task"
            required
            disabled={!canManageGovernance}
          />
          )}

          {canManageGovernance && isEditing && newTask.revisedDate !== (newTask.originalRevisedDate || '') && (
            <div className="border rounded-lg p-4 bg-yellow-50 mb-4">
              <Select
                label="Revised Date Change Reason"
                value={newTask.dueDateChangeReasonTag}
                onChange={(e) => setNewTask({ ...newTask, dueDateChangeReasonTag: e.target.value })}
                options={[
                  { value: 'scope_change', label: 'Scope Change' },
                  { value: 'other_priorities', label: 'Other Priorities' },
                  { value: 'resource_constraint', label: 'Resource Constraint' },
                  { value: 'external_dependency', label: 'External Dependency' },
                  { value: 'other', label: 'Other' }
                ]}
                required
              />
              <Input
                label="Reason Notes (Optional)"
                value={newTask.dueDateChangeReasonNotes}
                onChange={(e) => setNewTask({ ...newTask, dueDateChangeReasonNotes: e.target.value })}
                placeholder="Additional context"
              />
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={closeTaskModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {isEditing ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TasksPage;
